import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { roleAtLeast, type MeResponse, type Role } from '@jobshop/shared';
import { useMe } from '../api/hooks';
import { devEmail, signIn, signOutNow, watchAuth, type User } from './firebase';

interface Session {
  /** Firebase says who you are. */
  firebaseUser: User | null;
  /** The `users` collection says what you may do here. */
  me: MeResponse | undefined;
  loading: boolean;
  /** Signed in with Firebase but not authorised for this shop. */
  unauthorised: boolean;
  can(role: Role): boolean;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<Session | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [checked, setChecked] = useState(!!devEmail);

  useEffect(
    () =>
      watchAuth((user) => {
        setFirebaseUser(user);
        setChecked(true);
      }),
    [],
  );

  const signedIn = !!devEmail || !!firebaseUser;
  const meQuery = useMe(signedIn);

  const value = useMemo<Session>(
    () => ({
      firebaseUser,
      me: meQuery.data,
      loading: !checked || (signedIn && meQuery.isLoading),
      unauthorised: signedIn && meQuery.isError,
      can: (role) => (meQuery.data ? roleAtLeast(meQuery.data.role, role) : false),
      signIn,
      signOut: signOutNow,
    }),
    [firebaseUser, checked, signedIn, meQuery.data, meQuery.isLoading, meQuery.isError],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}
